import React, { useMemo, useState } from 'react';
import { Shift, UserProfile } from '../types';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { ShieldCheck, Mail, ShieldAlert, AlertTriangle, CheckCircle2, TrendingUp, Info, Trophy, Loader2 } from 'lucide-react';
import { supabase } from '../supabaseClient';

interface PerformanceViewProps {
    shifts: Shift[];
    userProfile: UserProfile | null;
    isPrivacyMode: boolean;
    isGoogleConnected: boolean;
    hasProviderToken: boolean;
    onConnectGoogle?: () => void;
    onProfileUpdate?: () => void;
    isPremiumOrVip?: boolean;
}

const COLORS = ['#F59E0B', '#EF4444', '#3B82F6', '#10B981', '#8B5CF6'];

export const PerformanceView: React.FC<PerformanceViewProps> = ({
    shifts,
    userProfile,
    isPrivacyMode,
    isGoogleConnected,
    hasProviderToken,
    onConnectGoogle,
    onProfileUpdate,
    isPremiumOrVip = false
}) => {
    const [showIntegrationInfo, setShowIntegrationInfo] = useState(false);
    const [manualScore, setManualScore] = useState<string>('');
    const [isSavingScore, setIsSavingScore] = useState(false);

    const handleSaveManualScore = async () => {
        if (!userProfile?.id || !manualScore) return;
        setIsSavingScore(true);
        try {
            const score = parseInt(manualScore, 10);
            if (isNaN(score) || score < 0 || score > 100) throw new Error('Invalid score');

            const { error } = await supabase
                .from('profiles')
                .update({ rhino_score: score })
                .eq('id', userProfile.id);

            if (error) throw error;

            if (onProfileUpdate) onProfileUpdate();
            setManualScore('');
        } catch (error) {
            console.error('Error saving manual score:', error);
            alert('Erro ao salvar. Digite um número de 0 a 100.');
        } finally {
            setIsSavingScore(false);
        }
    };

    // 1. XP Evolution Data (Group by Week)
    const xpData = useMemo(() => {
        const grouped: { [key: string]: number } = {};

        shifts.forEach(shift => {
            const month = shift.date.substring(0, 7); // YYYY-MM
            let shiftXp = 0;
            if (shift.type === 'WORK') shiftXp += 20;
            if (shift.type === 'OFF') shiftXp += 10;
            if (shift.noShow) shiftXp -= 100;
            if (shift.extraEarnings?.length > 0) shiftXp += 10;
            if (shift.expenses?.length > 0) shiftXp += 5;

            grouped[month] = (grouped[month] || 0) + Math.max(0, shiftXp); // ensure positive for simple view
        });

        return Object.entries(grouped)
            .sort((a, b) => a[0].localeCompare(b[0]))
            .slice(-6) // Last 6 months
            .map(([date, xp]) => ({ date, XP: xp }));
    }, [shifts]);

    // 2. Get real events from Rhino email or use score-based
    const currentScore = userProfile?.rhino_score ?? userProfile?.xp_points ?? 100;
    const rhinoEvents = userProfile?.rhino_events || [];
    
    // Use stored tier from Rhino email, fallback to score-based
    const rhinoTier = useMemo(() => {
        if (userProfile?.rhino_tier) {
            const tier = userProfile.rhino_tier;
            if (tier === 'Ouro') return { label: 'Ouro', color: 'text-yellow-400', bg: 'bg-yellow-400/10' };
            if (tier === 'Prata') return { label: 'Prata', color: 'text-slate-300', bg: 'bg-slate-300/10' };
            return { label: 'Bronze', color: 'text-amber-700', bg: 'bg-amber-700/10' };
        }
        // Fallback to score-based tier
        const tier = currentScore >= 95 ? 'Ouro' : currentScore >= 80 ? 'Prata' : 'Bronze';
        if (tier === 'Ouro') return { label: 'Ouro', color: 'text-yellow-400', bg: 'bg-yellow-400/10' };
        if (tier === 'Prata') return { label: 'Prata', color: 'text-slate-300', bg: 'bg-slate-300/10' };
        return { label: 'Bronze', color: 'text-amber-700', bg: 'bg-amber-700/10' };
    }, [currentScore, userProfile?.rhino_tier]);

    // Use real events from Rhino email if available
    const feedbacks = useMemo(() => {
        // If real events exist, use them
        if (rhinoEvents.length > 0) {
            return rhinoEvents.map(evt => ({
                text: evt.description,
                pointsLost: Math.abs(100 - evt.score),
                type: evt.type === 'comportamento' ? 'grave' : evt.type === 'aviso' ? 'aviso' : evt.type === 'multa' ? 'grave' : 'regular'
            }));
        }
        
        // Fallback to score-based mock
        if (currentScore >= 100) return [];

        const fbs = [];
        const missingPoints = 100 - currentScore;

        if (missingPoints >= 10) {
            fbs.push({ text: "Penalidade por procedimento.", pointsLost: 10, type: 'grave' });
        }
        if (missingPoints >= 5 && missingPoints < 10) {
            fbs.push({ text: "Aviso de limpeza.", pointsLost: 5, type: 'aviso' });
        }
        if (missingPoints > 0 && missingPoints < 5) {
            fbs.push({ text: "Atraso registrado.", pointsLost: missingPoints, type: 'aviso' });
        }

        return fbs;
    }, [currentScore, rhinoEvents]);

    const categorizeFeedback = (text: string) => {
        const lower = text.toLowerCase();
        if (lower.includes('comportamento') || lower.includes('atraso')) return 'Comportamento';
        if (lower.includes('limpeza') || lower.includes('sujeira')) return 'Limpeza';
        if (lower.includes('procedimento') || lower.includes('rota')) return 'Procedimento';
        return 'Outros';
    };

    const lossData = useMemo(() => {
        const categories: { [key: string]: number } = {};
        feedbacks.forEach(fb => {
            const cat = categorizeFeedback(fb.text);
            categories[cat] = (categories[cat] || 0) + fb.pointsLost;
        });
        return Object.entries(categories).map(([name, value]) => ({ name, value }));
    }, [feedbacks]);

    const formatPoints = (val: number | string) => isPrivacyMode ? '****' : val;

    return (
        <div className="space-y-6 max-w-4xl mx-auto pb-24">
            <header className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-8">
                <div>
                    <h2 className="text-3xl font-black text-white tracking-tight">Desempenho</h2>
                    <p className="text-slate-400 mt-2">Analise sua evolução e feedbacks da Rhino.</p>
                </div>
                {/* Visual Fix: Ensure manual value holds priority and is displayed here at the top */}
                <div className="bg-slate-800 border border-slate-700 rounded-2xl px-6 py-3 shrink-0 flex items-center gap-4">
                    <div>
                        <div className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">Rhino Score</div>
                        <div className="flex items-center gap-3">
                            <div className={`text-3xl font-black ${currentScore >= 95 ? 'text-green-400' :
                                currentScore >= 80 ? 'text-yellow-400' : 'text-red-400'
                                }`}>
                                {formatPoints(currentScore)}
                            </div>
                            <div className={`px-2 py-1 flex items-center gap-1 rounded border border-current ${rhinoTier.color} ${rhinoTier.bg}`}>
                                <Trophy className="w-4 h-4" />
                                <span className="text-xs font-bold tracking-wider uppercase">{rhinoTier.label}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Integrations Section */}
            <section className="bg-slate-800 border border-slate-700 rounded-3xl p-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl"></div>

                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 relative z-10">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <Mail className="w-6 h-6 text-blue-400" />
                            <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                Integração Rhino + Gmail
                            </h3>
                        </div>
                        <p className="text-slate-300 text-sm md:text-base max-w-lg mb-4">
                            Sincronize seu Tier e Feedbacks automaticamente lendo as notificações da Rhino.
                        </p>
                        <div className="flex flex-col gap-2 text-sm font-medium mt-1">
                            <div className="flex items-center gap-2">
                                <span>Status Google:</span>
                                {isGoogleConnected ? (
                                    <span className="flex items-center gap-1 text-green-400 bg-green-400/10 px-2.5 py-1 rounded-lg text-xs font-bold">
                                        <span className="w-1.5 h-1.5 rounded-full bg-green-400"></span>
                                        Conectado
                                    </span>
                                ) : (
                                    <span className="flex items-center gap-1 text-red-400 bg-red-400/10 px-2.5 py-1 rounded-lg text-xs font-bold">
                                        <span className="w-1.5 h-1.5 rounded-full bg-red-400"></span>
                                        Desconectado
                                    </span>
                                )}
                            </div>
                            {isGoogleConnected && (
                                <div className="flex items-center gap-2">
                                    <span>Leitura de Email:</span>
                                    {hasProviderToken ? (
                                        <span className="flex items-center gap-1 text-emerald-400 bg-emerald-400/10 px-2.5 py-1 rounded-lg text-xs font-bold">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                                            Ativa (Lendo Email Real)
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-1 text-amber-500 bg-amber-500/10 px-2.5 py-1 rounded-lg text-xs font-bold" title="Sua autorização de e-mail expirou ou você precisa re-sincronizar.">
                                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                                            Pendente (Clique em Sincronizar)
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {isGoogleConnected ? (
                        <div className="flex flex-col items-end gap-3 w-full md:w-auto mt-4 md:mt-0">
                            {hasProviderToken ? (
                                <button
                                    disabled
                                    className="bg-emerald-950/20 text-emerald-400 font-bold flex items-center justify-center gap-2 py-3 px-6 rounded-xl border border-emerald-900/40 cursor-default whitespace-nowrap w-full md:w-auto text-sm"
                                >
                                    <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                                    CONECTADO E ATIVO
                                </button>
                            ) : (
                                <button
                                    onClick={onConnectGoogle}
                                    className="bg-amber-600 hover:bg-amber-500 text-white font-bold flex items-center justify-center gap-2 py-3 px-6 rounded-xl transition-colors shadow-lg shadow-amber-900/20 whitespace-nowrap w-full md:w-auto text-sm animate-pulse"
                                >
                                    <Mail className="w-5 h-5" />
                                    SINCRONIZAR EMAIL GMAIL
                                </button>
                            )}

                            <div className="flex flex-col items-end mt-1 w-full max-w-[280px]">
                                <label className="text-[10px] text-slate-500 mb-1 uppercase font-bold tracking-wider">Lançamento Manual (Fallback)</label>
                                <div className="flex items-center gap-2 w-full">
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        placeholder="Ex: 95"
                                        value={manualScore}
                                        onChange={(e) => setManualScore(e.target.value)}
                                        className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm w-full focus:outline-none focus:border-blue-500 transition-colors"
                                    />
                                    <button
                                        onClick={handleSaveManualScore}
                                        disabled={!manualScore || isSavingScore}
                                        className="bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-white p-2 rounded-lg transition-colors border border-slate-600"
                                    >
                                        {isSavingScore ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Salvar'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-end gap-3 w-full md:w-auto mt-4 md:mt-0">
                            {/* Hide Google button temporarily since it's not working */}
                            {/* <button
                                onClick={() => setShowIntegrationInfo(!showIntegrationInfo)}
                                className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-6 rounded-xl transition-colors shadow-lg shadow-blue-900/20 whitespace-nowrap w-full md:w-auto"
                            >
                                CONECTAR CONTA GOOGLE
                            </button> */}

                            <div className="flex flex-col items-end mt-1 w-full max-w-[280px]">
                                <label className="text-[10px] text-slate-500 mb-1 uppercase font-bold tracking-wider">Lançamento Manual (Mock)</label>
                                <div className="flex items-center gap-2 w-full">
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        placeholder="Ex: 95"
                                        value={manualScore}
                                        onChange={(e) => setManualScore(e.target.value)}
                                        className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm w-full focus:outline-none focus:border-blue-500 transition-colors"
                                    />
                                    <button
                                        onClick={handleSaveManualScore}
                                        disabled={!manualScore || isSavingScore}
                                        className="bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-white p-2 rounded-lg transition-colors border border-slate-600"
                                    >
                                        {isSavingScore ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Salvar'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* UX Tip Alert */}
                {showIntegrationInfo && !isGoogleConnected && (
                    <div className="mt-6 bg-blue-900/30 border border-blue-500/30 rounded-2xl p-4 flex gap-4 animate-fade-in">
                        <Info className="w-6 h-6 text-blue-400 shrink-0" />
                        <div>
                            <p className="text-slate-200 text-sm md:text-base leading-relaxed">
                                <strong className="text-blue-300 block mb-1">Fique tranquilo!</strong>
                                O Driver Score utiliza IA <b>apenas</b> para filtrar e-mails enviados por <span className="text-blue-200 bg-blue-900/50 px-1 rounded font-mono">drivers-notifications@go-rhino.com</span>. Suas mensagens pessoais e outras conversas permanecem 100% privadas e inacessíveis para o sistema.
                            </p>
                            <button
                                onClick={() => {
                                    setShowIntegrationInfo(false);
                                    onConnectGoogle?.();
                                }}
                                className="mt-3 text-sm text-blue-400 hover:text-blue-300 underline font-medium"
                            >
                                Entendi, quero conectar
                            </button>
                        </div>
                    </div>
                )}
            </section>

            {/* Graphs Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 border border-slate-700 rounded-3xl p-6">
                    <div className="flex items-center gap-2 mb-6">
                        <TrendingUp className="w-5 h-5 text-green-400" />
                        <h3 className="font-bold text-white">Evolução de XP (Últimos Meses)</h3>
                    </div>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={xpData}>
                                <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                                <Tooltip
                                    cursor={{ fill: '#334155' }}
                                    contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '12px', color: '#fff' }}
                                    formatter={(value: any) => [`${formatPoints(value)} XP`, 'Progresso']}
                                />
                                <Bar dataKey="XP" fill="#10B981" radius={[6, 6, 0, 0]} barSize={40} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="bg-slate-800 border border-slate-700 rounded-3xl p-6">
                    <div className="flex items-center gap-2 mb-6">
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                        <h3 className="font-bold text-white">Maior Perda de Pontos</h3>
                    </div>
                    {lossData.length > 0 && currentScore < 100 ? (
                        <div className="h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={lossData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={90}
                                        paddingAngle={5}
                                        dataKey="value"
                                    >
                                        {lossData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '12px', color: '#fff' }}
                                        formatter={(value: any) => [`${formatPoints(value)} pts perdidos`, 'Penalidade']}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="flex flex-wrap justify-center gap-4 mt-2">
                                {lossData.map((entry, index) => (
                                    <div key={entry.name} className="flex items-center gap-2 text-xs text-slate-300">
                                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                                        {entry.name}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="h-64 flex flex-col items-center justify-center text-center">
                            <ShieldCheck className="w-16 h-16 text-green-500/50 mb-4" />
                            <p className="text-slate-400 text-sm">Nenhuma infração registrada no período base!</p>
                            <p className="text-green-400 font-bold mt-2">Score Perfeito 🌟</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Simulated Feedback Listing */}
            <div>
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <ShieldAlert className="w-5 h-5 text-slate-400" />
                    Observações Oficiais (Rhino)
                </h3>

                <div className="space-y-3">
                    {feedbacks.length > 0 ? (
                        feedbacks.map((fb, idx) => (
                            <div
                                key={idx}
                                className={`p-4 rounded-2xl border flex items-start gap-4 ${fb.type === 'grave'
                                    ? 'bg-red-500/10 border-red-500/20'
                                    : 'bg-yellow-500/10 border-yellow-500/20'
                                    }`}
                            >
                                <div className={`p-2 rounded-full shrink-0 ${fb.type === 'grave' ? 'bg-red-500/20 text-red-500' : 'bg-yellow-500/20 text-yellow-500'}`}>
                                    <AlertTriangle className="w-5 h-5" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-slate-200 text-sm leading-relaxed">{fb.text}</p>
                                    <div className="mt-2 flex items-center gap-2">
                                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${fb.type === 'grave' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                            -{formatPoints(fb.pointsLost)} pontos
                                        </span>
                                        <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">
                                            {fb.type === 'grave' ? 'Infração Grave' : 'Aviso Leve'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-12 bg-slate-800/50 rounded-3xl border border-slate-700 border-dashed">
                            <p className="text-slate-400">Tudo limpo por aqui! Nenhuma mensagem de alerta encontrada.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
